'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { useMutationLock } from '@/providers/mutation-lock';

// EWM-612 — the single processing indicator for cart mutations (add / remove / coupon
// / shipping / address). Replaces the per-control disable/enable, which flickered and
// looked inconsistent. The blurred backdrop pointer-blocks the viewport for the duration
// of the write and reads as a deliberate "working" state rather than a flashing overlay.
// It sits at z-1000 — ABOVE the drawer layer (panel z-999, scrim z-345) — so it also
// covers cart writes triggered from inside a drawer (quick-order tray / order upload).
//
// a11y: it's a transient, non-interactive, auto-dismissing busy state — NOT a focusable
// dialog — so it follows the codebase's loading-shell precedent (role="status" +
// aria-busy + aria-live), not role="dialog"/aria-modal (which would imply focus
// management this overlay neither does nor needs).
//
// It surfaces the moment a cart write starts and stays up for at least MIN_VISIBLE_MS,
// even if the write resolves sooner — a sub-second write must not flash the modal on and
// straight back off (the "flaky screen" the old show-delay caused). A write longer than
// the window simply stays up until it settles. Quantity edits are optimistic (EWM-610)
// and never raise the 'cart' scope, so they don't show this.
const MIN_VISIBLE_MS = 1200;

const CartProcessingModal = () => {
  const translate = useTranslations();
  const { isMutating } = useMutationLock('cart');
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(0);

  // EWM-760 — verified NOT movable to adjust-state-during-render: the
  // "become visible" branch stamps `shownAtRef.current = Date.now()` before
  // calling setVisible(true). Both a ref WRITE and an impure Date.now() call
  // during render are themselves banned under the new rule set
  // (react-hooks/refs "Cannot access refs during render" +
  // react-hooks/purity "Cannot call impure function during render") — so
  // hoisting this out would trade one violation for two others. This is a
  // genuine external-system sync (mirrors the mutation-lock's isMutating
  // into a debounced, minimum-visible-window UI state via a real timer) —
  // disabling in place rather than manufacturing an unsafe refactor.
  useEffect(() => {
    if (isMutating) {
      // A cart write is in flight — surface the modal immediately and stamp when, so the
      // minimum-visible window below is measured from first paint.
      if (!visible) {
        shownAtRef.current = Date.now();
        // eslint-disable-next-line react-hooks/set-state-in-effect -- EWM-760: genuine external-system sync (mutation-lock isMutating → debounced visibility with a real timer); see comment above the effect.
        setVisible(true);
      }
      return;
    }

    // The write settled. Keep the modal up for the remainder of the minimum-visible
    // window so a fast write doesn't flash it; a write longer than the window leaves
    // `remaining` at 0 and hides as soon as it finishes.
    if (!visible) return;
    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAtRef.current));
    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [isMutating, visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/20 backdrop-blur-sm"
      data-testid="cart-processing-modal"
    >
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="flex items-center gap-3 rounded-lg bg-white px-6 py-4 shadow-2xl"
      >
        <ArrowPathIcon className="text-primary size-6 animate-spin" aria-hidden />
        <span className="text-14 font-semibold text-gray-800">{translate('cart.updating')}</span>
      </div>
    </div>
  );
};

export default CartProcessingModal;
